'use client';
import Rating from '@/entities/Rating/ui/Rating';
import { Portal } from '@/shared/ui/Portal';
import ReviewPopup from '@/widgets/ReviewPopup/ui/ReviewPopup';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale/ru';
import { AnimatePresence } from 'framer-motion';
import Image from 'next/image';
import { FC, useState } from 'react';
import {
  cvaGallery,
  cvaGalleryItem,
  cvaGalleryItemImage,
  cvaGalleryItemLabel,
  cvaGalleryItemShowMore,
  cvaHeader,
  cvaName,
  cvaRoot,
  cvaText,
} from './ReviewPreviewStyles';

interface Props {
  rating: number;
  name: string;
  date?: Date;
  text: string;
  gallery: {
    image: string;
    before?: boolean;
    after?: boolean;
  }[];
}

const ReviewPreview: FC<Props> = ({ rating, name, date, text, gallery }) => {
  const [showMore, setShowMore] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);

  const onClickPreviewImage = (index: number) => {
    setShowGallery(true);
    setGalleryInitialIndex(index);
  };

  return (
    <div className={cvaRoot()}>
      <div className={cvaHeader()}>
        <div className={cvaName()}>
          {name}
          {date ? (
            <>
              ,{' '}
              {format(date, 'dd MMMM yyyy', {
                locale: ru,
              })}
            </>
          ) : null}
        </div>
        <Rating size={'small'} value={rating} />
      </div>
      <div className={cvaText()} dangerouslySetInnerHTML={{ __html: text }}></div>
      <div className={cvaGallery()}>
        {[...gallery].splice(0, showMore ? gallery.length : 4).map((item, index) => (
          <div className={cvaGalleryItem()} key={item.id} onClick={() => onClickPreviewImage(index)}>
            <Image className={cvaGalleryItemImage()} src={item.image} fill alt="" />
            {item.before || item.after ? (
              <div
                className={cvaGalleryItemLabel({
                  type: item.before ? 'before' : 'after',
                })}
              >
                {item.before ? 'До' : 'После'}
              </div>
            ) : null}
          </div>
        ))}
        {!showMore && gallery?.length > 4 ? (
          <div className={cvaGalleryItemShowMore()} onClick={() => setShowMore(true)}>
            + {gallery?.length - 4}
          </div>
        ) : null}
      </div>
      <AnimatePresence>
        {showGallery ? (
          <Portal>
            <ReviewPopup
              initialIndex={galleryInitialIndex}
              rating={rating}
              name={name}
              date={date}
              text={text}
              gallery={gallery}
              onClose={() => setShowGallery(false)}
            />
          </Portal>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default ReviewPreview;
